/* eslint-disable react-hooks/set-state-in-effect */
import { useTableView } from '../context/tableView';
import { Button } from 'primereact/button';
import { commonStrings } from '../../../i18n';
import styles from './tableViewControl.module.css';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import { BmweColumnMeta, BmweDataColumn } from '../../../types';
import { InputText } from 'primereact/inputtext';
import { useEffect, useState } from 'react';
import { FilterMode, TableView } from '../types/tableView';
import { confirmDialog } from 'primereact/confirmdialog';
import { tableStrings } from '../i18n/de-de';
import { Table } from '@tanstack/react-table';
import { ColumnFilterPopup } from './filterPin';
import { describeFilterValue } from '../filterSummary';

export function TableViewControlDialog<TRow extends Record<string, unknown>>({
  columns,
  table,
  close,
  isCreatingNew = false,
}: {
  columns: BmweDataColumn<TRow>[];
  table?: Table<TRow>;
  close: (reset: boolean) => void;
  isCreatingNew?: boolean;
}) {
  const {
    currentView,
    updateView,
    addView,
    saveView,
    deleteView,
    resetView,
    markSnapshotPreviewed,
    hasPreviewedSnapshot,
    columnFilters,
    sorting,
    setFilters,
    setSorting,
  } = useTableView();
  const [isNewView, setIsNewView] = useState(isCreatingNew);

  // --- START FIX ---
  // The logic for setting the initial tempView state was split between
  // useState and a useEffect, causing the "new view" logic to be overwritten.
  // We consolidate all initialization logic into this one useEffect.

  // 1. Initialize state simply. The effect will set the correct state.
  const [tempView, setTempView] = useState<TableView>(() =>
    structuredClone(currentView),
  );

  // 2. Use an effect to set the correct temporary state when the dialog
  //    opens (isCreatingNew changes) or the source view changes.
  useEffect(() => {
    const cloned = structuredClone(currentView);

    // A new view needs a name of its own and starts from the table's current filters.
    if (isCreatingNew) {
      cloned.name = '';
      cloned.filters = columnFilters;
      cloned.sorting = sorting;
    }

    // Initialize columnOrder if not present
    if (!cloned.columnOrder) {
      cloned.columnOrder = columns
        .map((col) => col.id ?? col.field ?? '')
        .filter(Boolean);
    }

    setTempView(cloned);
    setIsNewView(isCreatingNew);
  }, [currentView, isCreatingNew, columns, columnFilters, sorting]); // This effect now correctly handles the "new view" state
  // --- END FIX ---

  // Maintain ordered columns for drag-and-drop UI
  const [orderedColumns, setOrderedColumns] = useState<BmweDataColumn<TRow>[]>(
    () => columns,
  );

  // Sync orderedColumns with tempView.columnOrder
  useEffect(() => {
    if (!tempView.columnOrder || tempView.columnOrder.length === 0) {
      setOrderedColumns(columns);
      return;
    }

    const orderMap = new Map(
      tempView.columnOrder.map((id, index) => [id, index]),
    );
    const sorted = [...columns].sort((a, b) => {
      const idA = a.id ?? a.field ?? '';
      const idB = b.id ?? b.field ?? '';
      const indexA = orderMap.get(idA) ?? Number.MAX_SAFE_INTEGER;
      const indexB = orderMap.get(idB) ?? Number.MAX_SAFE_INTEGER;
      return indexA - indexB;
    });
    setOrderedColumns(sorted);
  }, [tempView.columnOrder, columns]);

  // Drag-and-drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [columnSearch, setColumnSearch] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const nameValue = tempView.name;
  const isNameMissing = isNewView && !nameValue.trim();

  const getColumnKey = (column: BmweDataColumn<TRow>) => {
    return column.id ?? column.field ?? '';
  };

  const getCheckedState = (column: BmweDataColumn<TRow>) => {
    const key = getColumnKey(column);
    if (!key) {
      return true;
    }

    return tempView.visibleColumns[key] ?? true;
  };

  const setColumnVisibility = (
    column: BmweDataColumn<TRow>,
    visible: boolean,
  ) => {
    const key = getColumnKey(column);
    if (!key) {
      return;
    }

    setTempView((prev) => {
      const updated = structuredClone(prev);
      updated.visibleColumns[key] = visible;
      return updated;
    });
  };

  const getDraftFilter = (id: string) =>
    tempView.filters?.find((filter) => filter.id === id)?.value;

  const setDraftFilter = (id: string, value: unknown) => {
    setTempView((prev) => ({
      ...prev,
      filters: [
        ...(prev.filters ?? []).filter((filter) => filter.id !== id),
        ...(value === undefined ? [] : [{ id, value }]),
      ],
    }));
  };

  const copyActiveFilters = () => {
    setTempView((prev) => ({ ...prev, filters: columnFilters, sorting }));
  };

  const handleNameChange = (name: string) => {
    setTempView((prev) => ({
      ...prev,
      name,
    }));
  };

  const normalizedSearch = columnSearch.trim().toLocaleLowerCase('de');
  const displayedColumns = orderedColumns.filter((column) => {
    if (showSelectedOnly && !getCheckedState(column)) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    return column.header.toLocaleLowerCase('de').includes(normalizedSearch);
  });

  const handleColumnReorder = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    const reorderedDisplayedColumns = [...displayedColumns];
    const [movedColumn] = reorderedDisplayedColumns.splice(fromIndex, 1);
    reorderedDisplayedColumns.splice(toIndex, 0, movedColumn);

    const displayedColumnIds = new Set(
      displayedColumns.map((column) => getColumnKey(column)),
    );
    let reorderedDisplayedIndex = 0;

    const newOrder = orderedColumns.map((column) => {
      if (!displayedColumnIds.has(getColumnKey(column))) {
        return column;
      }

      const reorderedColumn =
        reorderedDisplayedColumns[reorderedDisplayedIndex];
      reorderedDisplayedIndex += 1;
      return reorderedColumn;
    });

    setOrderedColumns(newOrder);

    // Update tempView with new column order
    const columnOrderIds = newOrder
      .map((col) => col.id ?? col.field ?? '')
      .filter(Boolean);

    setTempView((prev) => ({
      ...prev,
      columnOrder: columnOrderIds,
    }));
  };

  const moveColumnUp = (index: number) => {
    if (index > 0) {
      handleColumnReorder(index, index - 1);
    }
  };

  const moveColumnDown = (index: number) => {
    if (index < displayedColumns.length - 1) {
      handleColumnReorder(index, index + 1);
    }
  };

  // Drag-and-drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    // Drags inside a row's filter popup bubble here through the React portal.
    if (!e.currentTarget.contains(e.target as Node)) return;
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      handleColumnReorder(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const applyChanges = () => {
    if (isNewView) {
      return;
    }

    updateView(tempView);
    setFilters(tempView.filters ?? []);
    setSorting(tempView.sorting ?? []);
    markSnapshotPreviewed();
    close(false);
  };

  const startSaveAsNew = () => {
    setIsNewView(true);
    handleNameChange('');
  };

  const onSave = () => {
    if (isNewView) {
      if (isNameMissing) {
        return;
      }

      addView({
        canEdit: true,
        visibleColumns: tempView.visibleColumns,
        columnOrder: tempView.columnOrder,
        name: nameValue.trim(),
        filters: tempView.filters ?? [],
        filterMode: tempView.filterMode,
        sorting: tempView.sorting ?? [],
      });
      close(false);
      return;
    }

    saveView(tempView);
    close(false);
  };

  const confirmResetView = () => {
    confirmDialog({
      message: tableStrings.tableView.resetView.confirmResetMessage(
        currentView.name,
      ),
      header: tableStrings.tableView.resetView.header,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: tableStrings.tableView.resetView.label,
      rejectLabel: commonStrings.words.cancel,
      // Back to the state before the dialog first, then the reset on top.
      accept: () => {
        close(true);
        resetView(currentView.id);
      },
    });
  };

  const confirmDeleteView = () => {
    if (currentView.isSystem) {
      return;
    }

    confirmDialog({
      message: tableStrings.tableView.deleteView.confirmDeleteMessage(
        currentView.name,
      ),
      header: tableStrings.tableView.deleteView.header,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: commonStrings.words.delete,
      rejectLabel: commonStrings.words.cancel,
      acceptClassName: 'p-button-danger',
      accept: () => {
        deleteView(currentView.id);
        close(true);
      },
    });
  };

  return (
    <div className={styles.dialogBody}>
      <div className={styles.controlsWrapper}>
        <div className="form-grid">
          <label htmlFor="view-name">{tableStrings.tableView.name}</label>
          <InputText
            value={nameValue}
            id="view-name"
            required={isNewView}
            disabled={currentView.isSystem && !isNewView}
            onChange={(e) => {
              handleNameChange(e.target.value);
            }}
          />

          <span className={styles.filtersPseudoLabel}>
            {tableStrings.tableView.filtersConfig}
          </span>
          <div className={styles.filtersConfig}>
            <Button
              type="button"
              size="small"
              outlined
              icon="pi pi-filter"
              label={`${tableStrings.tableView.applyActiveFilters} (${tableStrings.tableView.activeFilterCount(columnFilters.length)})`}
              disabled={columnFilters.length === 0 && sorting.length === 0}
              onClick={copyActiveFilters}
            />
            <div
              role="radiogroup"
              aria-label={tableStrings.tableView.filterMode.label}
              className={styles.filterMode}
            >
              {(['and', 'or'] satisfies FilterMode[]).map((mode) => (
                <div key={mode} className={styles.columnFilterToggle}>
                  <RadioButton
                    inputId={`filter-mode-${mode}`}
                    name="filter-mode"
                    value={mode}
                    checked={(tempView.filterMode ?? 'and') === mode}
                    onChange={() =>
                      setTempView((prev) => ({ ...prev, filterMode: mode }))
                    }
                  />
                  <label htmlFor={`filter-mode-${mode}`}>
                    {tableStrings.tableView.filterMode[mode]}
                  </label>
                </div>
              ))}
            </div>
            <small className={styles.filtersHint}>
              {tableStrings.tableView.columnFiltersHint}
            </small>
          </div>

          <span className={styles.columnsPseudoLabel}>
            {tableStrings.tableView.columnsConfig}
          </span>
          <div className={styles.columnConfigWrapper}>
            <div className={styles.columnConfigTools}>
              <InputText
                value={columnSearch}
                className={styles.columnSearch}
                placeholder={tableStrings.tableView.searchColumns}
                onChange={(e) => setColumnSearch(e.target.value)}
              />
              <div className={styles.columnFilterToggle}>
                <Checkbox
                  inputId="show-selected-columns-only"
                  checked={showSelectedOnly}
                  onChange={({ checked }) => {
                    setShowSelectedOnly(checked ?? false);
                  }}
                />
                <label htmlFor="show-selected-columns-only">
                  {tableStrings.tableView.showSelectedOnly}
                </label>
              </div>
            </div>

            {displayedColumns.length === 0 && (
              <div className={styles.emptyState}>
                {tableStrings.tableView.noColumnsFound}
              </div>
            )}

            {displayedColumns.map((column, index) => {
              const id = column.id ?? column.field;
              if (!id) {
                return null;
              }
              const isVisible = getCheckedState(column);
              const tableColumn = table?.getColumn(id);
              const canFilter = tableColumn?.getCanFilter() ?? false;
              const filterValue = getDraftFilter(id);
              const filterSummary = canFilter
                ? describeFilterValue(
                    tableColumn?.columnDef.meta as BmweColumnMeta | undefined,
                    filterValue,
                  )
                : undefined;
              const isDragging = draggedIndex === index;
              const isDragOver = dragOverIndex === index;

              return (
                <div
                  key={id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`${styles.columnConfigItem} ${!isVisible ? styles.disabled : ''} ${isDragging ? styles.dragging : ''} ${isDragOver ? styles.dragOver : ''}`}
                >
                  <div
                    className={styles.dragHandle}
                    title="Ziehen zum Neuordnen"
                  >
                    <i className="pi pi-bars" aria-hidden="true" />
                  </div>
                  <div className={styles.columnConfigCheckbox}>
                    <Checkbox
                      inputId={`config-${id}`}
                      checked={isVisible}
                      onChange={({ checked }) => {
                        setColumnVisibility(column, checked ?? true);
                      }}
                    />
                  </div>
                  <div className={styles.columnConfigText}>
                    <label
                      htmlFor={`config-${id}`}
                      className={`${styles.columnConfigLabel} ${!isVisible ? styles.disabled : ''}`}
                    >
                      {column.header}
                    </label>
                    {filterSummary && (
                      <span
                        className={styles.columnFilterSummary}
                        title={filterSummary}
                      >
                        {filterSummary}
                      </span>
                    )}
                  </div>
                  <div className={styles.columnConfigActions}>
                    {tableColumn && canFilter ? (
                      <ColumnFilterPopup
                        column={tableColumn}
                        value={filterValue}
                        onApply={(value) => setDraftFilter(id, value)}
                        renderTrigger={(open) => (
                          <Button
                            type="button"
                            icon={
                              filterValue === undefined
                                ? 'pi pi-filter'
                                : 'pi pi-filter-fill'
                            }
                            size="small"
                            text
                            aria-haspopup="dialog"
                            aria-expanded={open}
                            aria-label={tableStrings.messages.filterBy(
                              column.header,
                            )}
                            title={tableStrings.messages.filterBy(
                              column.header,
                            )}
                          />
                        )}
                      />
                    ) : (
                      <span aria-hidden="true" />
                    )}
                    <Button
                      type="button"
                      icon="pi pi-arrow-up"
                      size="small"
                      text
                      disabled={index === 0}
                      onClick={() => moveColumnUp(index)}
                      aria-label={`${column.header} nach oben verschieben`}
                      title={`${column.header} nach oben verschieben`}
                    />
                    <Button
                      type="button"
                      icon="pi pi-arrow-down"
                      size="small"
                      text
                      disabled={index === displayedColumns.length - 1}
                      onClick={() => moveColumnDown(index)}
                      aria-label={`${column.header} nach unten verschieben`}
                      title={`${column.header} nach unten verschieben`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className={styles.dialogActionsWrapper}>
        <div className={styles.dialogActionsStart}>
          {!currentView.isSystem && !isNewView && (
            <Button
              type="button"
              label={commonStrings.words.delete}
              severity="danger"
              onClick={confirmDeleteView}
            />
          )}
          {currentView.hasOverride && !isNewView && (
            <Button
              type="button"
              label={tableStrings.tableView.resetView.label}
              title={tableStrings.tableView.resetView.title}
              severity="warning"
              outlined
              onClick={confirmResetView}
            />
          )}
        </div>
        <div className={styles.dialogActions}>
          {!isNewView && (
            <Button
              type="button"
              label={commonStrings.words.preview}
              outlined
              onClick={applyChanges}
            />
          )}
          {!isNewView && (
            <Button
              type="button"
              label={tableStrings.tableView.saveAsNew}
              outlined
              onClick={startSaveAsNew}
            />
          )}
          <Button
            type="button"
            label={commonStrings.words.save}
            disabled={isNameMissing}
            onClick={onSave}
          />

          <Button
            type="button"
            label={commonStrings.words.cancel}
            severity="danger"
            outlined
            onClick={() => {
              close(!hasPreviewedSnapshot);
            }}
          />
        </div>
      </div>
    </div>
  );
}
